/**
 * DUEL Coach — Completion Tracking & Weekly Check-in
 * AsyncStorage only. No DB. No migrations.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';

const completionKey = (userId: string, weekNum: number) =>
  `duel_coach_completion_${userId}_w${weekNum}`;
const checkinKey = (userId: string, weekNum: number) =>
  `duel_coach_checkin_${userId}_w${weekNum}`;

export type WorkoutFeedback = 'too_easy' | 'just_right' | 'too_hard';

export interface DayCompletion {
  dayIndex: number;
  dayLabel: string;
  completedAt: string; // ISO string
  exercisesCompleted: number;
  exercisesTotal: number;
}

export interface WeekCompletion {
  weekNumber: number;
  days: DayCompletion[];
  checkInFeedback?: WorkoutFeedback;
}

export async function saveWorkoutCompletion(
  userId: string,
  weekNumber: number,
  dayIndex: number,
  dayLabel: string,
  exercisesCompleted: number,
  exercisesTotal: number,
): Promise<void> {
  const key = completionKey(userId, weekNumber);
  const raw = await AsyncStorage.getItem(key);
  const existing: WeekCompletion = raw
    ? JSON.parse(raw)
    : { weekNumber, days: [] };

  // Upsert day
  const idx = existing.days.findIndex((d) => d.dayIndex === dayIndex);
  const entry: DayCompletion = {
    dayIndex,
    dayLabel,
    completedAt: new Date().toISOString(),
    exercisesCompleted,
    exercisesTotal,
  };
  if (idx >= 0) {
    existing.days[idx] = entry;
  } else {
    existing.days.push(entry);
  }
  await AsyncStorage.setItem(key, JSON.stringify(existing));
}

export async function loadWeekCompletion(
  userId: string,
  weekNumber: number,
): Promise<WeekCompletion | null> {
  const raw = await AsyncStorage.getItem(completionKey(userId, weekNumber));
  return raw ? JSON.parse(raw) : null;
}

export async function saveCheckInFeedback(
  userId: string,
  weekNumber: number,
  feedback: WorkoutFeedback,
): Promise<void> {
  const key = completionKey(userId, weekNumber);
  const raw = await AsyncStorage.getItem(key);
  const existing: WeekCompletion = raw
    ? JSON.parse(raw)
    : { weekNumber, days: [] };
  existing.checkInFeedback = feedback;
  await AsyncStorage.setItem(key, JSON.stringify(existing));
  // Also store separately for quick lookup
  await AsyncStorage.setItem(checkinKey(userId, weekNumber), feedback);
}

export async function loadCheckInFeedback(
  userId: string,
  weekNumber: number,
): Promise<WorkoutFeedback | null> {
  const raw = await AsyncStorage.getItem(checkinKey(userId, weekNumber));
  return (raw as WorkoutFeedback) || null;
}

/**
 * Get volume adjustment multiplier for next week based on check-in.
 * too_easy: +10% volume
 * just_right: no change
 * too_hard: -15% volume
 */
export function getVolumeAdjustment(feedback: WorkoutFeedback | null): number {
  if (feedback === 'too_easy') return 1.10;
  if (feedback === 'too_hard') return 0.85;
  return 1.0;
}

/**
 * Completion rate for a week (0–1)
 */
export function weekCompletionRate(completion: WeekCompletion | null, totalDays: number): number {
  if (!completion || totalDays === 0) return 0;
  return completion.days.length / totalDays;
}
